<template>
  <el-card v-if="!removed" shadow="never" class="articleItem" :style="customStyle" @click.native="openArticleDialog(item)">
    <div class="articelUser">
      <div class="userAvatar" @click.stop="handleUserClick">
        <!--
          头像走 CDN 缩略图（原图平均 176KB，这里只显示 36px），失败自动退回原图，
          首屏前几条不懒加载 —— 这些都在 <smart-img> 内部，见组件注释。
        -->
        <smart-img
          mode="avatar"
          :src="item.userInfoVO && item.userInfoVO.avatar"
          :alt="item.userInfoVO && item.userInfoVO.nickname"
          :eager="isAboveFold"
          width="36"
          height="36"
        />
      </div>
      <div class="userInfoTitle">
        <div
          class="userInfoName"
          :style="{ cursor: notAnonymous ? 'pointer' : 'default' }"
          @click.stop="handleUserClick"
        >
          <span class="name-text"
            >{{ item.userInfoVO.nickname }}
            <span class="nick-type" v-if="!notAnonymous"
              ><svg-icon icon-class="nickred" size="18"
            /></span></span>
        </div>
        <!-- 学校读 authorSchoolName（发帖时快照），不读 userInfoVO.school ——
             后者是作者当前的资料学校，作者一改教育经历，历史帖子显示的学校会跟着全变。
             老帖子没快照时服务端已回退成当前学校，这里不用再兜。 -->
        <div class="userSchool" v-if="notAnonymous && item.authorSchoolName">
          {{ item.authorSchoolName }}
          <!-- 匿名身份不展示学校认证角标：角标是实名背书，挂在匿名马甲上等于泄露身份。
               authorSchoolVerified 已经由服务端校验过「快照学校 == 作者当前认证学校」 -->
          <verified-badge
            :verified="item.authorSchoolVerified"
            :size="14"
          />
        </div>
      </div>
    </div>

    <div class="articleInfo" @click="openArticleDialog(item)">
      <div class="articleInfo-item">
        <div>
          <div class="article-title-style">{{ item.title }}</div>
          <p class="article-content-style">{{ item.content }}</p>
          <!-- 视频帖：avatar 里只有一张封面，显示成视频块，点开进播放器（不是图片预览） -->
          <VideoCoverBlock
            v-if="isVideo"
            :article="item"
            variant="feed"
            :eager="isAboveFold"
            @click.native.stop="openArticleDialog(item)"
          />
          <div
            class="cover-container"
            v-else-if="item.avatarList && item.avatarList.length > 0"
          >
            <div
              class="img-content"
              @click="(e) => e.stopPropagation()"
              v-for="(img, imgIndex) in displayImages"
              :key="imgIndex"
            >
              <!--
                这里显示的是 CDN 缩略图（格子只有 138px，原图却是 268KB 的 1079x1920），
                但 preview-src-list 传的是原图数组 —— 全屏预览要看清细节。

                注意：刻意**不加** :lazy。el-image 的懒加载是 mounted 时用
                isInContainer(getBoundingClientRect) 判一次 + 监听 scroll，而列表容器在
                骨架屏期间是 display:none，此时 rect 全是 0、判定为「不在视口」，
                之后不滚动就再也不会触发 —— 首屏配图会一直空着。
              -->
              <el-image
                :src="contentThumb(img)"
                :style="imageStyle"
                alt="文章图片"
                :preview-src-list="item.avatarList"
                @error="onContentThumbError(img)"
              />
              <div
                v-if="hasMoreImages && imgIndex === 3"
                class="badge-more"
                @click.stop="openArticleDialog(item)"
              >
                +{{ item.avatarList.length - 4 }}
              </div>
            </div>
          </div>
          <!-- 投票：图片之后、操作栏之前，与移动端卡片和详情页保持同一版式。
               PollView 内部已 @click.stop，点选项不会冒泡到卡片的「打开详情」 -->
          <PollView
            v-if="item.poll"
            :poll="item.poll"
            :article-id="item.id"
            @voted="onPollVoted"
          />
        </div>
      </div>
    </div>
    <div class="bottomItem">
      <div class="articleOhter">
        <span class="item" @click.stop="like(item)">
          <svg-icon
            :icon-class="item.isLike ? 'hearted' : 'heart'"
          />
          <span class="name">{{ formatViews(item.likeCount || 0) }}</span>
        </span>
        <span class="item" @click.stop="openArticleDialog(item)">
          <svg-icon icon-class="pingluncount" />
          <span class="name">{{ formatViews(item.commentCount) }}</span>
        </span>
        <span class="item" @click.stop="handleCollect(item)">
          <svg-icon
            :icon-class="item.isCollect ? 'starred' : 'star'"
          />
          <span class="name">{{ formatViews(item.collectCount) }}</span>
        </span>
      </div>
    </div>
    <el-divider class="articleItemBottomLine" v-if="hasBorder"></el-divider>
  </el-card>
</template>
<script>
import articleMixinx from '@/mixins/articleMixinx'
import aboveFoldMixin from '@/mixins/aboveFoldMixin'
import contentImageMixin from '@/mixins/contentImageMixin'
import PollView from '@/components/poll/PollView.vue'
import VideoCoverBlock from '@/components/video/VideoCoverBlock.vue'
import { isVideoArticle } from '@/utils/articleMedia'
export default {
  components: { PollView, VideoCoverBlock },
  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    articleLen: {
      type: Number,
      default: 0
    },
    index: {
      type: Number,
      default: 0
    },

    hasBorder: {
      type: Boolean,
      default: true
    },
    customStyle: {
      type: Object,
      default: () => ({})
    }
  },
  mixins: [articleMixinx, aboveFoldMixin, contentImageMixin],
  data () {
    return {
      // 在视频播放器里被作者删掉了：列表数据归父页面管，卡片自己先藏起来
      removed: false
    }
  },
  // 说明：这里原来注册了 ArticleDialog 组件但模板里并没有用到
  // （详情弹窗是 layout 上的全局单例 this.$root.$articleDialog），
  // 白白把弹窗及其依赖打进了列表卡片这个高频组件的依赖里。
  mounted () {
    // 监听文章数据同步事件
    this.$root.$on('article-data-sync', this.handleArticleSync)
    this.$root.$on('article-deleted', this.handleArticleDeleted)
  },
  beforeDestroy () {
    // 移除事件监听
    this.$root.$off('article-data-sync', this.handleArticleSync)
    this.$root.$off('article-deleted', this.handleArticleDeleted)
  },
  computed: {
    isVideo () {
      return isVideoArticle(this.item)
    },
    // 是否非匿名用户
    notAnonymous () {
      return this.item?.userInfoVO?.nickType === this.$enumType.isAnonymous.NO
    },
    // 说明：这里原有 userLevel / levelImage 两个 computed，模板里并没有用到
    // （等级徽章的 DOM 早就不在模板里，只剩下 .user-level 的样式）。
    // levelImage 用的是 require(`@/assets/image/level${n}.png`) 这种动态表达式，
    // webpack 会为它建一个 context module，把 assets/image 下 49 张等级图（约 1.2MB）
    // 全部打进产物 —— 一张都不会被渲染。两个 computed 一并移除。
    // 显示的图片列表（最多4张）
    displayImages () {
      return this.item.avatarList?.slice(0, 4) || []
    },
    // 是否有更多图片
    hasMoreImages () {
      return this.item.avatarList?.length > 4
    },
    // 图片样式 - 统一尺寸
    imageStyle () {
      return {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }
    }
  },
  methods: {
    // 处理文章数据同步
    handleArticleSync (data) {
      if (data.id === this.item.id) {
        // 逐字段判空再写：投票同步只带 { id, poll }，
        // 无条件赋值会把 likeCount / isLike 等一并写成 undefined
        const fields = ['likeCount', 'isLike', 'collectCount', 'isCollect', 'commentCount', 'poll']
        fields.forEach(field => {
          if (data[field] !== undefined) {
            this.$set(this.item, field, data[field])
          }
        })
      }
    },
    handleArticleDeleted (data) {
      if (data && String(data.id) === String(this.item.id)) this.removed = true
    },
    /**
     * 投票成功：写回本条数据并广播给详情弹窗、移动端卡片。
     * poll 是服务端算好的一整份（票数/占比/是否已投），整体替换而不是本地加减。
     */
    onPollVoted (poll) {
      if (!poll) return
      this.$set(this.item, 'poll', poll)
      this.$root.$emit('article-data-sync', { id: this.item.id, poll })
    },
    // 打开文章详情弹窗函数。带上整条数据：视频帖由弹窗直接转给视频播放器，首帧就有封面和文案
    openArticleDialog () {
      this.$root.$articleDialog.openDialog(this.item.id, '', this.item)
    },
    // 点击用户区域跳转到个人中心
    handleUserClick () {
      // 如果是匿名用户，不跳转
      if (!this.notAnonymous) {
        return
      }
      // 在新标签页打开个人中心
      if (this.item?.userInfoVO?.id) {
        const routeUrl = this.$router.resolve(`/user/${this.item.userInfoVO.id}`)
        window.open(routeUrl.href, '_blank')
      }
    }
  }
}
</script>
<style scoped lang="scss">
.articleItem {
  position: relative;
  padding: 16px 16px 0 16px;
  border-radius: 0;
  background-color: var(--background-color);
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  contain: content; // 限制浏览器重排范围，避免单个卡片变化引起全局重绘
  // 这里原本还有一条 will-change: transform。
  // will-change 是「即将变化」的提示，常驻在列表项上会让每张卡片都被提升为独立
  // 合成层：信息流是无限滚动，滚到几百条时显存/内存占用会明显上涨，低端安卓机上
  // 反而更容易掉帧。卡片本身没有 transform 动画，这条提示纯属负担，已移除。

  .articelUser {
    width: 100%;
    height: 36px;
    display: flex;

    .userAvatar {
      width: 36px;
      height: 36px;
      z-index: 10;
      cursor: pointer;
      margin-right: 10px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .userInfoTitle {
      display: flex;
      flex-direction: column;
      justify-content: space-around;

      .userInfoName {
        font-size: 14px;
        color: rgb(51, 51, 51);
        margin-bottom: 4px;
        
        .name-text {
          position: relative;
          display: inline-flex;
          align-items: center;

          .nick-type {
            margin-left: 4px;
            display: inline-flex;
            align-items: center;
          }
          
          .user-level {
            position: absolute;
            left: calc(100% + 4px);
            top: -5px;
            
            .level-img {
              width: 36px;
              height: auto;
            }
          }
        }
      }

      .userSchool {
        font-size: 12px;
        color: rgb(153, 153, 153);
        line-height: 1.2;
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }

  .articleInfo {
    margin-top: 10px;
    color: var(--article-color);
    margin-bottom: 15px;
    overflow: hidden;
    width: 100%;
    
    .articleInfo-item {
      display: flex;
      justify-content: space-between;
      width: 100%;

      > div {
        width: 100%;
        overflow: hidden;
      }

      a {
        color: var(--article-color);
      }

      h3 {
        position: relative;
        display: inline-block;
      }

      .article-title-style {
        word-break: break-word;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
        max-width: 100%;
        font-size: 18px;
        font-weight: 700;
        color: #333;
        margin-bottom: 4px;
      }

      .article-content-style {
        word-break: break-word;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: pre-wrap;
        max-width: 100%;
      }

      p {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        line-height: 24px;
        word-break: break-word;
        word-wrap: break-word;
      }

      .cover-container {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 8px 0;
        box-sizing: border-box;

        .img-content {
          position: relative;
          width: 138px;
          height: 138px;
          flex-shrink: 0;
          border-radius: 8px;
          overflow: hidden;

          @media screen and (max-width: $bp-md - 1px) {
            width: calc((100% - 16px) / 3);
            height: auto;
            aspect-ratio: 1 / 1;
          }
          @media screen and (max-width: $bp-sm - 1px) {
            width: calc((100% - 8px) / 2);
          }
        }
        .badge-more {
          position: absolute;
          right: 4px;
          bottom: 4px;
          min-width: 24px;
          height: 24px;
          padding: 0 6px;
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          font-size: 13px;
          border-radius: 12px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: background 0.3s;
          &:hover {
            background: rgba(0, 0, 0, 0.45);
          }
        }
        .el-image {
          width: 100%;
          height: 100%;
          border-radius: 8px;
          ::v-deep img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }
  }

  .bottomItem {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 16px;
    .articleOhter {
      display: flex;
      align-items: center;
      gap: 40px;

      .item {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.3s;
        color: #666;
        
        &:hover {
          transform: scale(1.05);
          color: #333;
        }

        svg {
          width: 18px;
          height: 18px;
          margin-right: 6px;
          flex-shrink: 0;
        }

        .name {
          font-size: 14px;
          line-height: 1;
          color: inherit;
        }
      }
    }
  }

  .articleItemBottomLine {
    margin: 16px 0;
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
